import { useEffect, useState } from "react";
import { X, Package, Calendar, User, FileText, Printer, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/dates";
import { CATEGORY_LABELS } from "../lib/categories";
import { groupByCategory } from "../lib/sections";
const fmtMoney = (n) => `$${(n || 0).toFixed(2)}`;
import { StatusBadge } from "../pages/_shared";
import { useAuth } from "../context/AuthContext";

/**
 * Reusable order-details modal used across Admin / Franchisee / Employee portals.
 *
 * Props:
 *   orderId          — required; fetches full order server-side
 *   onClose          — required
 *   allowEmployeeComplete — Employee flow: shows Fulfilled column + "Mark Picked Up" CTA
 *   onSaved          — called after status change (parent re-fetches list)
 */
export default function OrderDetailsDialog({ orderId, onClose, allowEmployeeComplete, onSaved }) {
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [fulfilled, setFulfilled] = useState({});    // { item_id: qty }
  const [dirty, setDirty] = useState(false);
  const [locations, setLocations] = useState([]);
  const [fulfillLoc, setFulfillLoc] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isEmployee = user?.role === "employee" || (!!user?.warehouse && !!allowEmployeeComplete);
  const isAdmin = user?.role === "admin";
  const ordersUrl = user?.warehouse && allowEmployeeComplete ? "/orders?queue=fulfillment" : "/orders";

  // Fulfillment checklist state — used at the "sent_to_production" stage so
  // the kitchen can physically tick off each line as they pull it.
  const [checklist, setChecklist] = useState({});
  const [priceMap, setPriceMap] = useState({});
  useEffect(() => {
    if (!orderId || user?.role === "employee") return;
    api.get("/inventory/order-catalog").then((r) => {
      const m = {};
      for (const i of r.data) { m[i.id] = i.price || 0; m[`${i.category}|${i.name.toLowerCase()}`] = i.price || 0; }
      setPriceMap(m);
    }).catch(() => {});
  }, [orderId, user?.role]);

  useEffect(() => {
    if (!orderId) return;
    api.get(ordersUrl).then((r) => {
      const found = (r.data || []).find((o) => o.id === orderId);
      if (!found) return;
      const seed = {};
      for (const it of found.items) seed[it.item_id] = !!it.pulled;
      setChecklist(seed);
    }).catch(() => {});
  }, [orderId]);

  useEffect(() => {
    if (!orderId) { setOrder(null); return; }
    setErr(""); setBusy(false);
    api.get(ordersUrl).then((r) => {
      const found = (r.data || []).find((o) => o.id === orderId);
      if (!found) { setErr("Order not found"); return; }
      setOrder(found);
      setFulfillLoc(found.fulfillment_location_id || "");
      setInternalNotes(found.internal_notes || "");
      const seed = {};
      for (const it of found.items) seed[it.item_id] = it.fulfilled_quantity ?? it.quantity;
      // Unsaved kitchen edits survive scroll, close/reopen and refresh until saved.
      let draft = {};
      try { draft = JSON.parse(localStorage.getItem(`fulfill-draft-${orderId}`) || "{}"); } catch { draft = {}; }
      for (const k of Object.keys(draft)) if (k in seed) seed[k] = draft[k];
      setFulfilled(seed);
      setDirty(Object.keys(draft).length > 0);
    }).catch((e) => setErr(e.message));
    // Admins need the list of inventory locations for the fulfillment picker
    if (user?.role === "admin") {
      api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    }
  }, [orderId, user?.role]);

  if (!orderId) return null;
  if (!order && !err) {
    return <Backdrop onClose={onClose}><div className="p-10 text-center text-sm text-muted-foreground">Loading…</div></Backdrop>;
  }
  if (err && !order) {
    return <Backdrop onClose={onClose}><div className="p-10 text-center text-sm text-destructive">{err}</div></Backdrop>;
  }

  // Group by category for a scannable read
  const groups = groupByCategory(order.items);
  const showPrice = user?.role !== "employee";
  const priceOf = (it) => it.unit_price ?? priceMap[it.item_id] ?? priceMap[`${it.category}|${(it.name || "").toLowerCase()}`] ?? 0;

  // Employee action mapping: current status → next status + button label
  const EMP_TRANSITIONS = {
    sent_to_production: { next: "pulled",           label: "Mark Pulled" },
    pulled:             { next: "ready_for_pickup", label: "Mark Ready for Pickup" },
    ready_for_pickup:   { next: "picked_up",        label: "Mark Picked Up" },
  };
  const empAction = isEmployee && allowEmployeeComplete ? EMP_TRANSITIONS[order.status] : null;
  const showEmployeeAction = !!empAction;
  // Employees can edit fulfilled quantities at any active-work stage (client Feb 2026 launch:
  // "when they get an order, they should be able to edit in case inventory is off").
  const showFulfillEditor = showEmployeeAction && ["sent_to_production", "pulled"].includes(order.status);
  // Show the physical "pulling checklist" only when the employee is at the sent_to_production stage.
  const showChecklist = showEmployeeAction && order.status === "sent_to_production";
  const checklistDone = order.items.every((it) => checklist[it.item_id]);
  const lineQty = (it) => (showFulfillEditor ? (fulfilled[it.item_id] ?? it.quantity) : (it.fulfilled_quantity ?? it.quantity)) || 0;
  const grand = groups.reduce((a, g) => {
    g.items.forEach((it) => { a.qty += it.quantity || 0; a.ful += lineQty(it); a.amt += lineQty(it) * priceOf(it); });
    return a;
  }, { qty: 0, ful: 0, amt: 0 });

  const saveFulfillment = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.patch(`/orders/${order.id}/fulfillment`, { fulfilled_quantities: fulfilled });
      localStorage.removeItem(`fulfill-draft-${order.id}`);
      setDirty(false);
      if (r.data?.items) setOrder(r.data);
      toast.success("Fulfilled quantities saved");
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const advanceEmp = async () => {
    if (!empAction) return;
    setBusy(true); setErr("");
    try {
      // If any fulfilled qty differs from the original ordered qty, persist those
      // adjustments FIRST via the dedicated endpoint so admin gets a notification.
      const hasAdjustments = showFulfillEditor && order.items.some((it) => {
        const req = fulfilled[it.item_id];
        return req !== undefined && Number(req) !== it.quantity;
      });
      if (hasAdjustments) {
        await api.patch(`/orders/${order.id}/fulfillment`, {
          fulfilled_quantities: fulfilled,
        });
      }
      await api.patch(`/orders/${order.id}/status`, {
        status: empAction.next,
        ...(showFulfillEditor ? { fulfilled_quantities: fulfilled } : {}),
        fulfillment_location_id: fulfillLoc || undefined,
      });
      localStorage.removeItem(`fulfill-draft-${order.id}`);
      toast.success(`${order.order_no} → ${empAction.label.replace("Mark ", "")}`);
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      api.get(ordersUrl).then((r) => { const f = (r.data || []).find((o) => o.id === order.id); if (f) setOrder(f); }).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  // Admin actions (fulfillment picker, internal notes) are editable pre-picked-up.
  const canAdminEdit = isAdmin && order.status !== "picked_up";
  const saveAdminEdits = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/orders/${order.id}`, {
        fulfillment_location_id: fulfillLoc || null,
        internal_notes: internalNotes || null,
      });
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Backdrop onClose={onClose}>
      <div className="p-6 border-b border-border flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5">
            <FileText className="h-3 w-3" /> Order Details
          </div>
          <h2 className="font-display text-2xl font-light tracking-tight">{order.order_no}</h2>
          <div className="flex items-center gap-2 mt-1.5 text-sm text-muted-foreground flex-wrap">
            <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{order.customer || order.location_name}</span>
            {order.pickup_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(order.pickup_date)}</span>}
            <StatusBadge status={order.status} />
          </div>
        </div>
        <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center shrink-0" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {groups.map((g) => (
          <section key={g.cat} data-testid={`details-section-${g.cat}`}>
            <div className="label-eyebrow mb-2">{CATEGORY_LABELS[g.cat]}</div>
            {/* Grid: [checklist? 36px] [name flex] [ordered 90px] [fulfilled 100px] */}
            <div className={`grid ${showChecklist ? "grid-cols-[36px_1fr_90px_100px]" : "grid-cols-[1fr_90px_100px]"} gap-3 px-2 py-1.5 label-eyebrow text-[10px] border-b border-border`}>
              {showChecklist && <div className="text-center">✓</div>}
              <div>Item</div>
              <div className="text-center">Ordered</div>
              <div className="text-center">Fulfilled</div>
            </div>
            <div className="divide-y divide-border">
              {g.items.map((it) => {
                const fq = showFulfillEditor ? (fulfilled[it.item_id] ?? it.quantity) : (it.fulfilled_quantity ?? it.quantity);
                const isChecked = !!checklist[it.item_id];
                return (
                  <div key={it.item_id} className={`grid ${showChecklist ? "grid-cols-[36px_1fr_90px_100px]" : "grid-cols-[1fr_90px_100px]"} gap-3 px-2 py-2.5 items-center ${showChecklist && isChecked ? "bg-[hsl(var(--accent-sage-soft))]/50" : ""}`} data-testid={`details-row-${it.item_id}`}>
                    {showChecklist && (
                      <button
                        type="button"
                        onClick={() => setChecklist((m) => ({ ...m, [it.item_id]: !m[it.item_id] }))}
                        data-testid={`checklist-${it.item_id}`}
                        aria-checked={isChecked}
                        role="checkbox"
                        className={`h-6 w-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                          isChecked
                            ? "bg-[hsl(var(--accent-sage))] border-[hsl(var(--accent-sage))] text-white"
                            : "border-border bg-card hover:border-foreground"
                        }`}
                      >
                        {isChecked && <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={3} />}
                      </button>
                    )}
                    <div className={`text-sm font-medium truncate ${showChecklist && isChecked ? "line-through opacity-60" : ""}`}>{it.name}</div>
                    <div className="text-center text-sm tabular-nums text-muted-foreground">{it.quantity}</div>
                    {showFulfillEditor ? (
                      <input
                        type="number" min="0" max={it.quantity}
                        value={fq}
                        onChange={(e) => {
                          const n = e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0);
                          setFulfilled((m) => {
                            const next = { ...m, [it.item_id]: Math.min(n, it.quantity) };
                            localStorage.setItem(`fulfill-draft-${order.id}`, JSON.stringify(next));
                            return next;
                          });
                          setDirty(true);
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        inputMode="numeric"
                        data-testid={`emp-fulfill-${it.item_id}`}
                        className="h-9 w-full rounded-lg border border-input bg-card px-2 text-center text-sm tabular-nums focus:border-primary focus:ring-2 focus:ring-primary/30"
                      />
                    ) : (
                      <div className={`text-center text-sm tabular-nums ${fq !== it.quantity ? "text-[hsl(var(--primary))] font-medium" : "text-muted-foreground"}`}>{fq}</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className={`grid ${showChecklist ? "grid-cols-[36px_1fr_90px_100px]" : "grid-cols-[1fr_90px_100px]"} gap-3 px-2 py-2 mt-1 rounded-lg bg-secondary/50 text-xs font-medium`} data-testid={`details-section-total-${g.cat}`}>
              {showChecklist && <div />}
              <div>{CATEGORY_LABELS[g.cat] || g.cat} total{showPrice && <span className="text-muted-foreground font-normal"> · {fmtMoney(g.items.reduce((s, it) => s + lineQty(it) * priceOf(it), 0))}</span>}</div>
              <div className="text-center tabular-nums">{g.items.reduce((s, it) => s + (it.quantity || 0), 0)}</div>
              <div className="text-center tabular-nums">{g.items.reduce((s, it) => s + lineQty(it), 0)}</div>
            </div>
          </section>
        ))}

        {groups.length > 0 && (
          <section className="flex items-center justify-between rounded-xl border border-border px-4 py-3" data-testid="details-grand-total">
            <span className="text-sm font-medium">Order total</span>
            <span className="text-sm tabular-nums">
              {grand.qty} ordered · {grand.ful} fulfilled{showPrice && <> · <b>{fmtMoney(grand.amt)}</b></>}
            </span>
          </section>
        )}

        {order.packaging_items?.length > 0 && (
          <section data-testid="details-section-packaging">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1.5"><Package className="h-3 w-3" /> Packaging Re-order</div>
            <div className="divide-y divide-border">
              {order.packaging_items.map((p, i) => (
                <div key={`${p.name}-${i}`} className="flex justify-between text-sm py-2 px-1">
                  <span className="truncate">{p.name}</span>
                  <span className="text-muted-foreground tabular-nums">× {p.quantity}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {order.notes && (
          <section>
            <div className="label-eyebrow mb-2">Notes</div>
            <p className="text-sm italic text-muted-foreground">&ldquo;{order.notes}&rdquo;</p>
          </section>
        )}

        {canAdminEdit && (
          <section data-testid="admin-order-edits" className="space-y-4 pt-2">
            <div>
              <label className="label-eyebrow mb-2 block">Fulfillment Location</label>
              <select
                value={fulfillLoc}
                onChange={(e) => setFulfillLoc(e.target.value)}
                data-testid="admin-fulfillment-location"
                className="h-11 w-full rounded-xl border border-input bg-card px-3 text-sm"
              >
                <option value="">— select kitchen —</option>
                {locations.filter((l) => l.is_warehouse).map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">
                When the order is marked picked up, inventory will auto-deduct from this location.
              </p>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Internal Notes (admin/kitchen only)</label>
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={2}
                data-testid="admin-internal-notes"
                placeholder="e.g. VIP customer, needs extra pistachio"
                className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={saveAdminEdits}
              disabled={busy}
              data-testid="save-admin-edits-btn"
              className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-secondary disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </section>
        )}

        {order.internal_notes && !canAdminEdit && (
          <section>
            <div className="label-eyebrow mb-2">Internal Notes</div>
            <p className="text-sm italic text-muted-foreground">{order.internal_notes}</p>
          </section>
        )}

        {isAdmin && order.shortage_review && (
          <div data-testid="order-shortage-flag"
            className={`text-xs rounded-lg px-3 py-2 border ${order.shortage_review.status === "pending" ? "border-[#B7791F]/40 bg-[#FFF8EB] text-[#B7791F]" : "border-border text-muted-foreground"}`}>
            {order.shortage_review.status === "pending"
              ? "Kitchen stock short at pickup — billable quantities need your review on the invoice. "
              : `Pickup shortage resolved by ${order.shortage_review.resolved_by}. `}
            {(order.pickup_shortages || []).map((x) => `${x.name}: ${x.transferred ?? x.moved} of ${x.fulfilled ?? x.requested} transferred`).join(" · ")}
          </div>
        )}

        {err && <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">{err}</div>}
      </div>

      {showEmployeeAction && (
        <div className="p-6 border-t border-border flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {showChecklist ? (
              checklistDone
                ? "All items pulled — ready to mark this batch as Pulled."
                : `Tick each item off as you pull it from the shelf (${order.items.filter((it) => checklist[it.item_id]).length}/${order.items.length}).`
            ) : (showFulfillEditor
              ? "Adjust fulfilled quantities based on what the kitchen actually produced."
              : "Confirm you've pulled the items from inventory and are starting production.")}
          </div>
          {showFulfillEditor && ["sent_to_production", "pulled"].includes(order.status) && (
            <button
              onClick={saveFulfillment}
              disabled={busy || !dirty}
              data-testid="emp-save-fulfillment-btn"
              className="h-11 px-4 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary disabled:opacity-40 shrink-0"
            >
              {dirty ? "Save Changes" : "Saved"}
            </button>
          )}
          <button
            onClick={advanceEmp}
            disabled={busy || (showChecklist && !checklistDone)}
            data-testid="emp-advance-status-btn"
            className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 shrink-0"
          >
            <CheckCircle2 className="h-4 w-4" /> {busy ? "Saving…" : empAction.label}
          </button>
        </div>
      )}
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4"
         onClick={onClose} data-testid="order-details-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-card rounded-2xl border border-border w-full max-w-2xl max-h-[92vh] flex flex-col"
           data-testid="order-details-dialog">
        {children}
      </div>
    </div>
  );
}
